import { z } from "zod";

export interface CarModel {
	id: string;
	name: string;
	brand: string;
	price: string; // Monthly price estimate
	type: "sedan" | "suv" | "ev" | "luxury";
	imageUrl: string;
	features: string[];
}

export interface ChatMessage {
	role: "user" | "model";
	text: string;
	timestamp: number;
}

export enum LoadingState {
	IDLE = "IDLE",
	LOADING = "LOADING",
	ERROR = "ERROR",
}

const phoneRegex = new RegExp(/^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/);

// 공통 필드를 포함하는 기본 스키마
const baseInquirySchema = z.object({
	inquirerName: z
		.string()
		.min(2, { message: "담당자 성함은 2자 이상 입력해주세요." })
		.max(20, { message: "담당자 성함은 20자 이내로 입력해주세요." })
		.regex(/^[가-힣a-zA-Z\s]+$/, {
			message: "담당자 성함은 한글 또는 영문으로 입력해주세요.",
		}),
	inquirerEmail: z
		.string()
		.email({ message: "올바른 이메일 주소를 입력해주세요." })
		.optional(),
	inquirerPhone: z
		.string()
		.regex(phoneRegex, { message: "올바른 전화번호 형식을 입력해주세요." })
		.optional(),
	inquiryMessage: z
		.string()
		.max(1000, "메시지는 1000자 이내로 입력해주세요.")
		.optional(),
	isPrivacyPolicyAgreed: z.boolean().refine((val) => val === true, {
		message: "개인정보 수집 및 이용에 동의해야 합니다.",
	}),
	isMarketingConsentAgreed: z.boolean().optional(),
	turnstileToken: z.string().min(1, { message: "보안 인증을 완료해주세요." }),
});

// 'project' 타입 문의 스키마
export const projectInquiryFormSchema = baseInquirySchema.merge(
	z.object({
		inquiryType: z.literal("project"),
		inquirerName: z
			.string()
			.min(2, { message: "회사명은 2자 이상 입력해주세요." })
			.max(20, { message: "회사명은 20자 이내로 입력해주세요." })
			.regex(/^[가-힣a-zA-Z\s]+$/, {
				message: "회사명은 한글 또는 영문으로 입력해주세요.",
			}),
		inquiryDetails: z.object({
			managerName: z
				.string()
				.min(2, { message: "담당자 성함은 2자 이상 입력해주세요." })
				.max(20, { message: "담당자 성함은 20자 이내로 입력해주세요." })
				.regex(/^[가-힣a-zA-Z\s]+$/, {
					message: "담당자 성함은 한글 또는 영문으로 입력해주세요.",
				}),
			projectType: z
				.string()
				.max(100, { message: "프로젝트 유형은 100자 이내로 입력해주세요." })
				.optional(),
			budget: z
				.string()
				.max(100, { message: "예산은 100자 이내로 입력해주세요." })
				.optional(),
			deadline: z
				.string()
				.max(100, { message: "희망 마감일은 100자 이내로 입력해주세요." })
				.optional(),
			referenceUrl: z
				.string()
				.url({ message: "올바른 URL을 입력해주세요." })
				.optional()
				.or(z.literal("")),
		}),
	}),
);

// 'contact' 타입 문의 스키마
export const contactInquiryFormSchema = baseInquirySchema.merge(
	z.object({
		inquiryType: z.literal("contact"),
		inquirerEmail: z
			.string()
			.email({ message: "올바른 이메일 주소를 입력해주세요." }),
		inquirerPhone: z
			.string()
			.regex(phoneRegex, { message: "올바른 전화번호 형식을 입력해주세요." }),
	}),
);

export const carInquiryFormSchema = baseInquirySchema.merge(
	z.object({
		inquiryType: z.literal("car"),
		inquirerName: z
			.string()
			.min(2, { message: "이름은 2자 이상 입력해주세요." })
			.max(20, { message: "이름은 20자 이내로 입력해주세요." })
			.regex(/^[가-힣a-zA-Z\s]+$/, {
				message: "이름은 한글 또는 영문으로 입력해주세요.",
			}),
		inquiryDetails: z.object({
			interestedCarModel: z
				.string()
				.min(2, { message: "관심 차종은 2자 이상 입력해주세요." })
				.max(20, { message: "관심 차종은 20자 이내로 입력해주세요." })
				.regex(/^[가-힣a-zA-Z\s]+$/, {
					message: "관심 차종은 한글 또는 영문으로 입력해주세요.",
				}),
		}),
	}),
);

// Discriminated union으로 두 스키마를 통합
export const inquiryFormSchema = z.discriminatedUnion("inquiryType", [
	projectInquiryFormSchema,
	contactInquiryFormSchema,
	carInquiryFormSchema,
]);

// 타입 정의
export type ProjectInquiryFormValues = z.infer<typeof projectInquiryFormSchema>;
export type ContactInquiryFormValues = z.infer<typeof contactInquiryFormSchema>;
export type CarInquiryFormValues = z.infer<typeof carInquiryFormSchema>;
export type InquiryFormValues = z.infer<typeof inquiryFormSchema>;
